import {observable, action} from 'mobx';
import {observer} from 'mobx-react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import React from 'react';

import {TabContent, TabPane, Nav, NavItem, NavLink} from 'reactstrap';

import inject from '../../hoc/injectHoc';

import mediaStickImage from '../../../assets/images/project-content-media-stick.png';
import {PAYMENT_PLAN_MONTHLY, PAYMENT_PLAN_LIFETIME} from '../../../constants/subscriptionPlanConstants';

import CreditCounter from './components/common/CreditCounter';
import FeatureList from './components/common/FeatureList';

import './pricingTabs.scss';

const SUBSCRIPTION_TAB = 'subscription';
const LIFETIME_TAB = 'lifetime';
const MAX_CREDIT_COUNT = 99;
const SUBSCRIPTION_PRICE = 3500;
const LIFETIME_PRICE = 75000;

/**
 * The MediaStickPricingTabs component.
 */
export class MediaStickPricingTabs extends React.Component {
  /**
   * Pricing view that is showing
   *
   * @type {boolean}
   */
  @observable activePricingTab = SUBSCRIPTION_TAB;

  /**
   * How many media stick credits the user would like to purchase
   *
   * @type {number}
   */
  @observable creditCount = 1;

  /**
   * Triggered when the component is first mounted to the page.
   */
  @action componentDidMount() {
    const {tabSelected, startingCreditCount} = this.props;
    if (tabSelected) {
      this.selectActivePricingTab(tabSelected);
    }
    if (startingCreditCount) {
      this.creditCount = startingCreditCount;
    }
  }

  /**
   * Selects the active pricing tab
   *
   * @param {string} pricingTabName
   */
  @action selectActivePricingTab(pricingTabName) {
    this.activePricingTab = pricingTabName;
  }

  /**
   * Updates the Credit Count
   *
   * @param {number} newCreditCount
   */
  @action updateCredit(newCreditCount) {
    const validCreditCount = newCreditCount <= MAX_CREDIT_COUNT && newCreditCount >= 0;
    if (validCreditCount) {
      this.creditCount = newCreditCount;
    }
  }

  /**
   * Renders the Media Banner
   * @param {number} pricePerCredit
   * @param {'monthly' | 'lifetime'} paymentSchedule
   * @returns {{}}
   */
  renderMediaBanner(pricePerCredit, paymentSchedule) {
    const {onPurchase} = this.props;
    return (
      <div className="media-stick-banner">
        <div className="banner-header">Project Content Media Player - Easily Create & Schedule Content for any Interior Sign</div>
        <div className="media-stick-banner-content">
          <img
            className="media-stick-image"
            src={mediaStickImage}
            alt="Project Content Media Stick"
          />
          <FeatureList
            features={[
              'Plug-N-Play',
              'Schedule from anywhere',
              'Create & schedule your own content',
              '2-year warranty',
              'Tech support & training included'
            ]}
          />
          <div className="px-2">
            <CreditCounter
              quantity={this.creditCount}
              pricePerCredit={pricePerCredit}
              updateCredit={(newCredit) => this.updateCredit(newCredit)}
              paymentSchedule={paymentSchedule}
              title="Player Total"
              unit="player"
            />
            {paymentSchedule === PAYMENT_PLAN_MONTHLY && (
              <div className="d-flex justify-content-center">
                <small className="text-muted">(24 month commitment)</small>
              </div>
            )}
            <button
              type="button"
              className="btn purchase-button"
              onClick={() => onPurchase(paymentSchedule, this.creditCount)}
              disabled={false}
            >
              Purchase
            </button>
          </div>
        </div>
      </div>);
  }

  /**
   * Renders the component.
   *
   * @returns {{}}
   */
  render() {
    return (
      <>
        <Nav tabs className="pricing-tabs mt-5">
          <h5 className="pricing-title">Interior Displays</h5>
          <NavItem>
            <NavLink
              className={classNames({active: this.activePricingTab === SUBSCRIPTION_TAB})}
              onClick={() => this.selectActivePricingTab(SUBSCRIPTION_TAB)}
            >
              Subscription <br />
              <span className="subtitle">Monthly</span>
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classNames({active: this.activePricingTab === LIFETIME_TAB})}
              onClick={() => this.selectActivePricingTab(LIFETIME_TAB)}
            >
              Pay Upfront <br />
              <span className="subtitle">Best Value</span>
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={this.activePricingTab} className="pt-3  media-stick-subscription-tab">
          <TabPane tabId={SUBSCRIPTION_TAB}>
            {this.renderMediaBanner(SUBSCRIPTION_PRICE, PAYMENT_PLAN_MONTHLY)}
          </TabPane>
          <TabPane tabId={LIFETIME_TAB}>
            {this.renderMediaBanner(LIFETIME_PRICE, PAYMENT_PLAN_LIFETIME)}
          </TabPane>
        </TabContent>
      </>
    );
  }
}

MediaStickPricingTabs.propTypes = {
  onPurchase: PropTypes.func.isRequired,
  startingCreditCount: PropTypes.number,
  tabSelected: PropTypes.string,
};

MediaStickPricingTabs.wrappedComponent = {};
MediaStickPricingTabs.wrappedComponent.propTypes = {
};

export default inject(MediaStickPricingTabs)(
  observer(MediaStickPricingTabs)
);
